/**
 * Change proposals — the words and the rules both platforms share.
 *
 * A trainer cannot write to a client's plan or goal; they propose, the client
 * accepts or declines, and only acceptance changes anything. This module holds
 * what the web pages and the mobile screens on either side of that exchange
 * would otherwise each restate: how a proposal is named to the client, how an
 * answer that hit drift is told apart from one that failed, what each side is
 * told once it lands, and how the trainer's edits become a payload.
 *
 * Plain functions over shared types, so both platforms test them without a
 * renderer. The React state stays with each platform's hooks.
 */

import {
  API_ERROR_CODES,
  ApiErrorClass,
  isResourceStaleError,
} from '../../types/api/errors';
import { safeValidateProposeGoalChange } from '../../validation/trainer.schemas';
import { getStatusGroup } from '../goal/goal-list';

import type { StaleDetails } from '../../types/api/errors';
import type {
  AttentionProposal,
  AttentionProposals,
  ChangeProposal,
  ChangeProposalDiffRow,
  ChangeProposalKind,
  ChangeProposalSubject,
} from '../../types/domain/trainer.types';
import type { UserGoal, UserGoals } from '../../types/domain/user-goal.types';
import type {
  ProposeGoalChangeSchemaInput,
  ProposePlanChangeSchemaInput,
} from '../../validation/trainer.schemas';

// =============================================================================
// THE CLIENT'S SIDE — reading and answering
// =============================================================================

/** The review surface's words, web's verbatim. */
export const PROPOSAL_REVIEW_COPY = {
  title: 'A change from your trainer',
  kicker: 'Waiting on you',
  note: 'Why',
  proposed: {
    title: 'What would change',
    toggle: 'Show what your trainer changed',
  },
  drift: {
    title: 'What you have changed since',
    toggle: 'Show what you changed',
  },
  driftNotice: {
    title: 'This was written before your changes',
    body: "Going ahead replaces what you changed with your trainer's version. Keeping yours declines this one, and they can send an updated change.",
  },
  failedTitle: 'That did not go through',
  answered: 'You have already answered this one.',
  rescinded:
    'Your trainer withdrew this change, so there is nothing to answer.',
  expired:
    'This change expired before it was answered. Ask your trainer to send it again.',
  notFound: 'This change does not exist, or it was not sent to you.',
  backLabel: 'Back to dashboard',
  accept: 'Accept',
  decline: 'Decline',
  acceptOverDrift: "Use my trainer's version",
  keepMine: 'Keep mine',
  showDetail: 'Show detail',
  hideDetail: 'Hide detail',
} as const;

/** `generated` is absent on a trainer's proposal read before the field existed. */
type MaybeGenerated = Partial<Pick<ChangeProposal, 'generated'>>;

/** The review surface's words, whichever set a proposal reads in. */
export type ProposalReviewCopy = {
  [
    K in keyof typeof PROPOSAL_REVIEW_COPY
  ]: (typeof PROPOSAL_REVIEW_COPY)[K] extends string
    ? string
    : { [P in keyof (typeof PROPOSAL_REVIEW_COPY)[K]]: string };
};

/**
 * The words for the schedule the app suggests with a newly generated plan
 * (`generated: true`). No trainer sent it, so nothing here names one or says
 * anyone has been told. The expired and withdrawn lines are the API's 409
 * wording for this kind, as the trainer set's are for theirs.
 */
export const GENERATED_SCHEDULE_REVIEW_COPY: ProposalReviewCopy = {
  ...PROPOSAL_REVIEW_COPY,
  title: 'A suggested schedule',
  note: 'About this',
  proposed: {
    title: PROPOSAL_REVIEW_COPY.proposed.title,
    toggle: 'Show the suggested sessions',
  },
  driftNotice: {
    title: PROPOSAL_REVIEW_COPY.driftNotice.title,
    body: 'Going ahead replaces what you changed with the suggested schedule. Keeping yours declines it.',
  },
  rescinded:
    'A newer plan replaced this suggested schedule, so there is nothing to answer.',
  expired:
    'This suggested schedule has expired. You can still set your own schedule on the plan.',
  acceptOverDrift: 'Use the suggested schedule',
};

/**
 * Which set of words a proposal reads in. Null (still loading) reads in the
 * trainer's, which every proposal used before the app could suggest one.
 */
export function proposalReviewCopy(
  proposal: MaybeGenerated | null | undefined
): ProposalReviewCopy {
  return proposal?.generated
    ? GENERATED_SCHEDULE_REVIEW_COPY
    : PROPOSAL_REVIEW_COPY;
}

/**
 * What one diff row does: adds a value, removes one, or changes it.
 *
 * The API marks a missing side with an em dash (`—`). A suggested schedule is
 * all additions — every row would otherwise read "— → Week 1 - Strength A",
 * a struck-through dash and an arrow for each new session — so the row shows
 * only the side that exists.
 */
export type DiffRowChange =
  | { kind: 'added'; value: string }
  | { kind: 'removed'; value: string }
  | { kind: 'changed'; before: string; after: string };

const isEmptyDiffSide = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed === '' || trimmed === '—';
};

export function diffRowChange(
  row: Pick<ChangeProposalDiffRow, 'before' | 'after'>
): DiffRowChange {
  if (isEmptyDiffSide(row.before)) return { kind: 'added', value: row.after };
  if (isEmptyDiffSide(row.after)) return { kind: 'removed', value: row.before };
  return { kind: 'changed', before: row.before, after: row.after };
}

/** What a proposal is about, in the sentence that names it. */
export const PROPOSAL_SUBJECT_NOUN: Record<ChangeProposalKind, string> = {
  plan_workouts: 'the workouts in',
  plan_schedule: 'the schedule for',
  user_goal: 'the target on',
};

/**
 * Who sent it: the handle, matching every other surface in this feature.
 *
 * Both halves of the party are nullable, because the resource reads them off a
 * relation that does not survive a deleted account — and "@null" on the one
 * screen a client is asked to consent on is worse than an unnamed sender. The
 * whole party is null on a suggested schedule; its screens never name a
 * sender (see {@link proposalHeadline}), so this is only a guard.
 */
export function proposalSender(
  proposal: Pick<ChangeProposal, 'trainer'>
): string {
  return proposal.trainer?.username
    ? `@${proposal.trainer.username}`
    : 'Your trainer';
}

/**
 * The review surface's headline, e.g. "@coach_kim suggested a change to the
 * workouts in Strength — Wave 3", or "A suggested schedule for 8-Week
 * Personalised Plan" for the one the app suggests.
 *
 * `subject_name` is null once the plan or goal has been deleted. The proposal
 * is unanswerable then, but it still has to read as a sentence rather than
 * trailing off.
 */
export function proposalHeadline(
  proposal: Pick<ChangeProposal, 'trainer' | 'kind' | 'subject_name'> &
    MaybeGenerated
): string {
  const about = PROPOSAL_SUBJECT_NOUN[proposal.kind] ?? 'something in';
  const subject = proposal.subject_name ?? 'something you were following';

  if (proposal.generated) return `A suggested schedule for ${subject}`;

  return `${proposalSender(proposal)} suggested a change to ${about} ${subject}`;
}

/**
 * The proposal is not there for this reader: it does not exist, or it was not
 * sent to them. A 404 means exactly that and nothing else — the API answers
 * every status, withdrawn and expired included, with a 200, because
 * withdrawing changes the status and never deletes the row. Read as "not
 * available" rather than as a failure to load.
 */
export function isProposalGone(error: unknown): boolean {
  return error instanceof ApiErrorClass && error.status === 404;
}

/** Only a pending proposal can be answered; every other status is history. */
export function isProposalAnswerable(
  proposal: Pick<ChangeProposal, 'status'>
): boolean {
  return proposal.status === 'pending';
}

/**
 * What the review surface says in place of the actions once a proposal is no
 * longer waiting — by status, because withdrawn and expired are not "already
 * answered". Null while it is pending.
 *
 * Status is worked out by the API at read time, so a pending proposal past its
 * `expires_at` already reads `expired`; never re-derive it from the clock.
 * The withdrawn and expired lines are the API's own 409 wording, so the
 * screen and a refused answer say the same thing.
 */
export function proposalClosedLine(
  proposal: Pick<ChangeProposal, 'status'> & MaybeGenerated
): string | null {
  const copy = proposalReviewCopy(proposal);
  switch (proposal.status) {
    case 'pending':
      return null;
    case 'rescinded':
      return copy.rescinded;
    case 'expired':
      return copy.expired;
    default:
      return copy.answered;
  }
}

export type ProposalAnswer = 'accept' | 'decline';

/** Why an answer did not land. */
export type ProposalRespondFailure =
  /**
   * The client changed the subject after the proposal was written. Not a
   * failure: the question changes to "theirs or yours", and only they can say.
   */
  | { kind: 'drift'; drift: StaleDetails }
  /**
   * `outdated` when what the screen shows is no longer true: the proposal
   * stopped waiting while it was open (a 409 — withdrawn, expired or answered
   * elsewhere), or the plan or goal it was about has been deleted (a 404 —
   * not the read's "not yours" 404). Either way the message is the API's,
   * written to be shown, and the screen should read the proposal again.
   */
  | { kind: 'error'; message: string; outdated: boolean };

const ANSWER_FAILED = 'Unable to answer this change.';

/**
 * Tell drift apart from a failure.
 *
 * Keyed on the error code rather than the 409, which also answers three other
 * refusals on this endpoint. A stale error carrying no parsed drift is still
 * stale — it falls through to the message rather than opening an empty "here
 * is what moved" screen.
 */
export function readRespondFailure(error: unknown): ProposalRespondFailure {
  if (isResourceStaleError(error) && error.drift) {
    return { kind: 'drift', drift: error.drift };
  }

  return {
    kind: 'error',
    message:
      error instanceof Error && error.message ? error.message : ANSWER_FAILED,
    outdated:
      error instanceof ApiErrorClass &&
      (error.code === API_ERROR_CODES.RESOURCE_CONFLICT ||
        error.status === 404),
  };
}

/**
 * What the client is told once their answer lands.
 *
 * Accepting changed their own record, so the line says which one. Declining
 * changed nothing, so the line says who now knows — except on a suggested
 * schedule, where nobody does, so it says what they can do instead.
 */
export function proposalAnsweredMessage(
  proposal: Pick<ChangeProposal, 'trainer' | 'kind'> & MaybeGenerated,
  answer: ProposalAnswer
): string {
  // Nobody sent a suggested schedule, so nobody is told it was declined.
  if (proposal.generated) {
    return answer === 'decline'
      ? 'Declined. You can still set your own schedule on the plan.'
      : 'Accepted — the sessions are on your calendar.';
  }

  if (answer === 'decline') {
    return `Declined. ${proposalSender(proposal)} has been told.`;
  }

  return proposal.kind === 'user_goal'
    ? 'Accepted — your goal has been updated.'
    : 'Accepted — your plan has been updated.';
}

// =============================================================================
// THE TRAINER'S SIDE — sending
// =============================================================================

/** What a trainer is told once a proposal is on its way, whatever its kind. */
export function proposalSentMessage(username: string): string {
  return `Sent to @${username}. Nothing changes until they accept.`;
}

/** What a trainer is told when a proposal did not go. */
export function proposalSendFailedMessage(username: string): string {
  return `Could not send the change to @${username}. Please try again.`;
}

/**
 * The API's own words for a refusal, when they are written to be read: its
 * 409s (a change already waiting on that client, a proposal already withdrawn,
 * expired or answered) and 422s (a deadline that has already passed). Null for
 * anything else, which the caller words itself. A 402 never reaches here: the
 * app's paywall answers it.
 */
export function proposalRefusalMessage(error: unknown): string | null {
  if (
    error instanceof ApiErrorClass &&
    (error.status === 409 || error.status === 422) &&
    error.message
  ) {
    return error.message;
  }

  return null;
}

/** What a trainer is told when a proposal did not go: the API's words, or ours. */
export function proposalSendErrorMessage(
  error: unknown,
  username: string
): string {
  return proposalRefusalMessage(error) ?? proposalSendFailedMessage(username);
}

/** The plan editor's words while it proposes. */
export const PLAN_PROPOSAL_COPY = {
  sendLabel: 'Send to client',
  noteSubject: 'these changes',
  sendFailed: 'Could not send the change. Please try again.',
  switchToSchedule: 'Change which days instead',
  switchToWorkouts: 'Change the workouts instead',
  /**
   * The API refused to show this trainer the plan: they did not prescribe it,
   * or their contract with the client has ended. The editor never opens on a
   * plan it could not load — an empty form would propose replacing theirs
   * with nothing.
   */
  unavailable:
    'You can only propose a change to a plan you prescribed, while your contract with them is in force.',
  backToCoaching: 'Back to coaching',
} as const;

/**
 * The API will not show this trainer the client's plan: a 404 from the
 * template read (they did not prescribe it, or their contract has ended) or a
 * 403 from the plan and schedule reads (a former trainer). Either way there is
 * nothing to propose on, and retrying will not change that.
 */
export function isPlanOutOfReach(error: unknown): boolean {
  return (
    error instanceof ApiErrorClass &&
    (error.status === 403 || error.status === 404)
  );
}

/**
 * The proposal on this subject still waiting on the client, or null — read
 * from their `GET /trainer/clients/{username}` (or a feed row: the slots are
 * built by the same code).
 *
 * The API holds one pending proposal per subject per client and 409s a second,
 * so a trainer told only once they press Send would have edited a whole plan
 * for nothing. The slots carry the most recent proposal, not only live ones,
 * so the status is checked here rather than by every caller. Null slots (a
 * contract that is not live) read as nothing waiting: the propose call is
 * refused then anyway, with its own words.
 */
export function pendingProposal(
  read: { proposals: AttentionProposals | null } | null | undefined,
  subject: ChangeProposalSubject
): AttentionProposal | null {
  const proposal = read?.proposals?.[subject] ?? null;
  return proposal?.status === 'pending' ? proposal : null;
}

/** The title over a proposal editor that a waiting proposal keeps shut. */
export const PROPOSAL_WAITING_TITLE = 'A change is already waiting';

/**
 * Why a proposal editor will not open: "A change to Build Strength is already
 * waiting on them. Withdraw it before proposing another."
 *
 * Names the subject where it can. `subject_name` is null once the plan or goal
 * has been deleted — the proposal outlives it — so it falls back to the kind.
 */
export function proposalWaitingMessage(
  proposal: Pick<AttentionProposal, 'subject_name' | 'goal_id'>
): string {
  const subject =
    proposal.subject_name ??
    (proposal.goal_id === null ? 'their plan' : 'one of their goals');

  return `A change to ${subject} is already waiting on them. Withdraw it before proposing another.`;
}

/** The plan editor's title while it proposes, e.g. "Adjust @sam's plan". */
export function planProposalTitle(username: string): string {
  return `Adjust @${username}'s plan`;
}

export interface PlanProposalInput {
  planId: string;
  note: string;
  workouts: ProposePlanChangeSchemaInput['workouts'];
  /** The cadence as the editor leaves it. */
  deloadEveryWeeks: number | null | undefined;
  /** The cadence on the client's plan as it was loaded. */
  savedDeloadEveryWeeks: number | null | undefined;
}

/**
 * The plan proposal payload.
 *
 * The whole edited plan is the payload — `workouts` is exactly what the editor
 * already produces. The cadence only rides along when the trainer changed it:
 * an omitted key leaves the client's cadence alone, while null would clear it.
 */
export function buildPlanProposal({
  planId,
  note,
  workouts,
  deloadEveryWeeks,
  savedDeloadEveryWeeks,
}: PlanProposalInput): ProposePlanChangeSchemaInput {
  const cadence = deloadEveryWeeks ?? null;
  const changed = cadence !== (savedDeloadEveryWeeks ?? null);

  return {
    plan_id: planId,
    note,
    workouts,
    ...(changed ? { deload_every_weeks: cadence } : {}),
  };
}

// -----------------------------------------------------------------------------
// Goal proposals
// -----------------------------------------------------------------------------

/** The goal proposal's words, web's verbatim where web has them. */
export const GOAL_PROPOSAL_COPY = {
  title: 'Propose a new target',
  lede: 'They decide whether to take this. Nothing changes until they accept.',
  goalLabel: 'Goal',
  goalPlaceholder: 'Choose a goal…',
  goalEmptyMatch: 'No goal by that name',
  targetLabel: 'New target',
  dateLabel: 'New target date',
  noteLabel: 'Why',
  noteHelp:
    'The client sees this beside the change. It is the only part that says why.',
  send: 'Send to client',
  cancel: 'Cancel',
  loadFailedTitle: "We couldn't load this goal",
  loadFailedBody: 'It may have been deleted since the card was loaded.',
  goalsFailedTitle: "We couldn't load their goals",
  noGoalsTitle: 'Nothing to propose against',
  targetRequired: 'Enter a new target',
} as const;

/** The longest note the API takes. */
export const PROPOSAL_NOTE_MAX = 530;

/** The picker's hint: whose goals these are, and what a proposal can touch. */
export function goalPickerHelp(username: string): string {
  return `One of @${username}'s current goals. A proposal changes its target or its date.`;
}

/** The empty state when a client has no goal to propose against. */
export function noProposableGoalsMessage(username: string): string {
  return `@${username} has no current goals. A proposal changes the target or date of one they already set.`;
}

/**
 * The goals a proposal can be made against: the current ones — active, and
 * ongoing (past their date, not finished) — that are not locked. An overdue
 * goal is the one a later date fixes, and these are the goals the client view
 * lists above the button that opens the picker. Finished and cancelled goals
 * are history.
 *
 * A locked goal can be proposed on, but the client's accept is refused (409),
 * so offering it would send a change nobody can take.
 */
export function proposableGoals(goals: UserGoals[]): UserGoals[] {
  return goals.filter((goal) => {
    if (goal.locked) return false;
    const group = getStatusGroup(goal.status);
    return group === 'active' || group === 'ongoing';
  });
}

/** The two fields a goal proposal can move, as the form holds them. */
export interface GoalProposalFields {
  /** As typed. */
  targetValue: string;
  /** A calendar day, `YYYY-MM-DD`. */
  targetDate: string;
}

/**
 * The fields seeded from the goal as it stands, so the inputs start where it
 * is and changing one has not implicitly changed the other.
 *
 * The API serialises dates at midnight UTC; a date input wants a plain calendar
 * day, so the date part is taken rather than reformatting an instant.
 */
export function goalProposalFields(
  goal: Pick<UserGoal, 'target_value' | 'target_date'>
): GoalProposalFields {
  return {
    targetValue: String(goal.target_value),
    targetDate: goal.target_date.slice(0, 10),
  };
}

/** Nothing moved — so there is nothing to ask the client to agree to. */
export function isGoalProposalUnchanged(
  seeded: GoalProposalFields,
  current: GoalProposalFields
): boolean {
  return (
    current.targetValue.trim() === seeded.targetValue &&
    current.targetDate === seeded.targetDate
  );
}

export type GoalProposalErrors = Partial<
  Record<'target_value' | 'target_date' | 'note', string>
>;

export type GoalProposalResult =
  | { ok: true; payload: ProposeGoalChangeSchemaInput }
  | { ok: false; errors: GoalProposalErrors };

/** A typed number, with a decimal comma read as a point. */
function parseTarget(value: string): number {
  return Number(value.trim().replace(',', '.'));
}

/**
 * The goal proposal payload, validated.
 *
 * Only what moved is sent. Both fields are optional to the API and at least
 * one is required, so posting an unchanged value would ask the client to agree
 * to something already true. A cleared target is an error rather than a
 * proposal of zero.
 */
export function buildGoalProposal(input: {
  goalId: string;
  note: string;
  seeded: GoalProposalFields;
  current: GoalProposalFields;
}): GoalProposalResult {
  const { goalId, note, seeded, current } = input;
  const targetMoved = current.targetValue.trim() !== seeded.targetValue;

  if (targetMoved && current.targetValue.trim() === '') {
    return {
      ok: false,
      errors: { target_value: GOAL_PROPOSAL_COPY.targetRequired },
    };
  }

  const payload: ProposeGoalChangeSchemaInput = {
    goal_id: goalId,
    note: note.trim(),
    ...(targetMoved ? { target_value: parseTarget(current.targetValue) } : {}),
    ...(current.targetDate !== seeded.targetDate
      ? { target_date: current.targetDate }
      : {}),
  };

  const result = safeValidateProposeGoalChange(payload);
  if (result.success) return { ok: true, payload };

  const errors: GoalProposalErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (
      (field === 'target_value' ||
        field === 'target_date' ||
        field === 'note') &&
      !(field in errors)
    ) {
      errors[field] = issue.message;
    }
  }

  return { ok: false, errors };
}
